import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../api/apiFetch";
import { formatNumber } from "../utils/format";
import { exportProductsToPDF } from "../utils/pdfExport";
import { exportLocationLabelsToPDF } from "../utils/locationLabelsPdf";
import { exportProductLabelsToPDF } from "../utils/productLabelsPdf";
import ErrorModal from "../components/ErrorModal";
import { usePermission } from "../hooks/usePermission";

export default function InventoryOverview() {
  const { hasPermission } = usePermission();

  // Permission checks
  const canViewStockCounts = hasPermission("stock_counts.view");
  const canViewPurchases = hasPermission("purchases.view");
  const canViewSales = hasPermission("sales.view");
  const canViewProducts = hasPermission("products.view");
  const canViewBrands = hasPermission("brands.view");
  const canViewLocations = hasPermission("locations.view");
  const canViewSuppliers = hasPermission("suppliers.view");
  const canExportStatistics = hasPermission("statistics.export");
  const canManageSnapshots = hasPermission("statistics.manage_snapshots");
  const [topSellersPeriod, setTopSellersPeriod] = useState('current_month');
  const [worstSellersPeriod, setWorstSellersPeriod] = useState('current_month');
  const [topSellers, setTopSellers] = useState([]);
  const [worstSellers, setWorstSellers] = useState([]);
  const [loadingTopSellers, setLoadingTopSellers] = useState(false);
  const [loadingWorstSellers, setLoadingWorstSellers] = useState(false);
  const [stockValue, setStockValue] = useState(null);
  const [loadingStockValue, setLoadingStockValue] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [brands, setBrands] = useState([]);
  const [exportOptions, setExportOptions] = useState({
    showEstimatedStock: false,
    showConfirmedStock: false,
    includeInactive: false,
    showManualColumn: true,
    showProductValue: false
  });
  const [selectedBrands, setSelectedBrands] = useState(new Set());
  const [exportingPDF, setExportingPDF] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });
  const [showLocationLabelsModal, setShowLocationLabelsModal] = useState(false);
  const [exportingLocationLabels, setExportingLocationLabels] = useState(false);
  const [locationLabelsLeafOnly, setLocationLabelsLeafOnly] = useState(true);
  const [exportingProductStickers, setExportingProductStickers] = useState(false);

  const periodLabels = {
    'today': 'Vandaag',
    'current_week': 'Huidige week',
    'last_week': 'Vorige week',
    'current_month': 'Huidige maand',
    'last_month': 'Vorige maand',
    'current_year': 'Huidig jaar',
    'last_year': 'Vorig jaar'
  };

  useEffect(() => {
    fetchTopSellers();
  }, [topSellersPeriod]);

  useEffect(() => {
    fetchWorstSellers();
  }, [worstSellersPeriod]);

  useEffect(() => {
    fetchStockValue();
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const data = await apiFetch('/brands');
      setBrands(data || []);
      // Initialize all brands as selected
      setSelectedBrands(new Set(data.map(b => b.id)));
    } catch (err) {
      console.error('Error fetching brands:', err);
      setBrands([]);
    }
  };

  const fetchTopSellers = async () => {
    setLoadingTopSellers(true);
    try {
      const data = await apiFetch(`/sales/statistics/top-products?period=${topSellersPeriod}&limit=10`);
      setTopSellers(data.top_sellers || []);
    } catch (err) {
      console.error('Error fetching top sellers:', err);
      setTopSellers([]);
    } finally {
      setLoadingTopSellers(false);
    }
  };

  const fetchWorstSellers = async () => {
    setLoadingWorstSellers(true);
    try {
      const data = await apiFetch(`/sales/statistics/top-products?period=${worstSellersPeriod}&limit=10`);
      setWorstSellers(data.worst_sellers || []);
    } catch (err) {
      console.error('Error fetching worst sellers:', err);
      setWorstSellers([]);
    } finally {
      setLoadingWorstSellers(false);
    }
  };

  const fetchStockValue = async () => {
    setLoadingStockValue(true);
    try {
      // Try to get latest snapshot first (fast)
      const snapshot = await apiFetch('/products/statistics/total-value/latest');

      if (snapshot) {
        // We have a snapshot, use it
        setStockValue(snapshot);
      } else {
        // No snapshot exists, create one
        console.log('No snapshot found, creating first snapshot...');
        const newSnapshot = await apiFetch('/products/statistics/total-value/snapshot', {
          method: 'POST'
        });
        setStockValue(newSnapshot);
      }
    } catch (err) {
      console.error('Error fetching stock value:', err);
      setStockValue(null);
    } finally {
      setLoadingStockValue(false);
    }
  };

  const refreshStockValue = async () => {
    setLoadingStockValue(true);
    try {
      // Create a new snapshot with current values
      const newSnapshot = await apiFetch('/products/statistics/total-value/snapshot', {
        method: 'POST'
      });
      setStockValue(newSnapshot);
    } catch (err) {
      console.error('Error refreshing stock value:', err);
    } finally {
      setLoadingStockValue(false);
    }
  };

  const handleExportClick = () => {
    // Just open the modal immediately
    setShowExportModal(true);
  };

  const handleExportConfirm = async () => {
    // Fetch products only when user confirms export
    setExportingPDF(true);
    try {
      const params = new URLSearchParams({
        include_inactive: exportOptions.includeInactive ? 'true' : 'false',
        limit: '10000'  // Fetch all products for export
      });
      const response = await apiFetch(`/products?${params}`);

      // Handle paginated response
      const allProducts = response.items || response;

      // Filter products by selected brands
      const filteredProducts = (allProducts || []).filter(p => selectedBrands.has(p.brand_id));

      // Generate PDF
      exportProductsToPDF(filteredProducts, exportOptions);

      setShowExportModal(false);
    } catch (err) {
      console.error('Error fetching products:', err);
      setErrorModal({ show: true, message: 'Fout bij ophalen van producten voor export' });
    } finally {
      setExportingPDF(false);
    }
  };

  const toggleBrand = (brandId) => {
    const newSelected = new Set(selectedBrands);
    if (newSelected.has(brandId)) {
      newSelected.delete(brandId);
    } else {
      newSelected.add(brandId);
    }
    setSelectedBrands(newSelected);
  };

  const toggleAllBrands = () => {
    if (selectedBrands.size === brands.length) {
      // Deselect all
      setSelectedBrands(new Set());
    } else {
      // Select all
      setSelectedBrands(new Set(brands.map(b => b.id)));
    }
  };

  const handleLocationLabelsExport = async () => {
    setExportingLocationLabels(true);
    try {
      const locations = await apiFetch('/locations');
      await exportLocationLabelsToPDF(locations, { leafOnly: locationLabelsLeafOnly });
      setShowLocationLabelsModal(false);
    } catch (err) {
      console.error('Error exporting location labels:', err);
      setErrorModal({ show: true, message: err.message || 'Fout bij exporteren van locatielabels' });
    } finally {
      setExportingLocationLabels(false);
    }
  };

  const handleProductStickersExport = async () => {
    setExportingProductStickers(true);
    try {
      const params = new URLSearchParams({
        include_inactive: 'false',
        limit: '10000',
      });
      const response = await apiFetch(`/products?${params}`);
      const allProducts = response.items || response;
      await exportProductLabelsToPDF(allProducts);
    } catch (err) {
      console.error('Error exporting product stickers:', err);
      setErrorModal({ show: true, message: err.message || 'Fout bij exporteren van productstickers' });
    } finally {
      setExportingProductStickers(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Voorraden</h1>
        <p className="text-gray-600">Overzicht van alle voorraadbeheer functionaliteiten</p>
      </div>

      {/* Main Tiles */}
      <div className="grid md:grid-cols-4 gap-6">
        {/* Voorraadtellingen Tile */}
        {canViewStockCounts ? (
          <Link
            to="/stock-counts"
            className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
          >
            <div className="text-blue-600 mb-4">
              <svg
                className="w-10 h-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Voorraadtellingen
            </h2>
            <p className="text-gray-600 text-sm">
              Beheer en bekijk voorraadtellingen
            </p>
          </Link>
        ) : (
          <div className="bg-white p-6 rounded-lg shadow-md opacity-60 cursor-not-allowed">
            <div className="text-blue-600 mb-4">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Voorraadtellingen</h2>
            <p className="text-gray-600 text-sm">Beheer en bekijk voorraadtellingen</p>
          </div>
        )}

        {/* Aankopen Tile */}
        {canViewPurchases ? (
          <Link
            to="/purchases"
            className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
          >
            <div className="text-green-600 mb-4">
              <svg
                className="w-10 h-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Aankopen
            </h2>
            <p className="text-gray-600 text-sm">
              Registreer en beheer inkopen
            </p>
          </Link>
        ) : (
          <div className="bg-white p-6 rounded-lg shadow-md opacity-60 cursor-not-allowed">
            <div className="text-green-600 mb-4">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Aankopen</h2>
            <p className="text-gray-600 text-sm">Registreer en beheer inkopen</p>
          </div>
        )}

        {/* Verkopen Tile */}
        {canViewSales ? (
          <Link
            to="/sales"
            className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
          >
            <div className="text-purple-600 mb-4">
              <svg
                className="w-10 h-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Verkopen
            </h2>
            <p className="text-gray-600 text-sm">
              Registreer en bekijk verkopen
            </p>
          </Link>
        ) : (
          <div className="bg-white p-6 rounded-lg shadow-md opacity-60 cursor-not-allowed">
            <div className="text-purple-600 mb-4">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Verkopen</h2>
            <p className="text-gray-600 text-sm">Registreer en bekijk verkopen</p>
          </div>
        )}

        {/* Beheer Tile */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Beheer</h2>
          <div className="grid grid-cols-2 gap-3">
            {/* Producten */}
            {canViewProducts ? (
              <Link
                to="/products"
                className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 transition-colors"
              >
                <div className="text-indigo-600 flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-900">Producten</span>
              </Link>
            ) : (
              <div className="flex items-center gap-2 p-2 rounded opacity-60 cursor-not-allowed">
                <div className="text-indigo-600 flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-900">Producten</span>
              </div>
            )}

            {/* Locaties */}
            {canViewLocations ? (
              <Link
                to="/locations"
                className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 transition-colors"
              >
                <div className="text-orange-600 flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-900">Locaties</span>
              </Link>
            ) : (
              <div className="flex items-center gap-2 p-2 rounded opacity-60 cursor-not-allowed">
                <div className="text-orange-600 flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-900">Locaties</span>
              </div>
            )}

            {/* Merken */}
            {canViewBrands ? (
              <Link
                to="/brands"
                className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 transition-colors"
              >
                <div className="text-pink-600 flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-900">Merken</span>
              </Link>
            ) : (
              <div className="flex items-center gap-2 p-2 rounded opacity-60 cursor-not-allowed">
                <div className="text-pink-600 flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-900">Merken</span>
              </div>
            )}

            {/* Leveranciers */}
            {canViewSuppliers ? (
              <Link
                to="/suppliers"
                className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 transition-colors"
              >
                <div className="text-teal-600 flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-900">Leveranciers</span>
              </Link>
            ) : (
              <div className="flex items-center gap-2 p-2 rounded opacity-60 cursor-not-allowed">
                <div className="text-teal-600 flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-gray-900">Leveranciers</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Attention Points and Quick Actions */}
      <div className="mt-8 grid md:grid-cols-2 gap-6">
        {/* Aandachtspunten - Products Running Low */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Aandachtspunten
          </h2>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Producten die bijna op zijn (gebaseerd op gemiddeld verbruik)</p>
            {/* TODO: Implement low stock products logic */}
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">Nog te implementeren</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Snelle Acties
          </h2>
          <div className="space-y-3">
            {canExportStatistics ? (
              <button
                onClick={handleExportClick}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="font-medium text-gray-900">Exporteer Productenlijst</p>
                  <p className="text-sm text-gray-500">Download PDF met alle producten</p>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <div className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 opacity-60 cursor-not-allowed">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="font-medium text-gray-900">Exporteer Productenlijst</p>
                  <p className="text-sm text-gray-500">Download PDF met alle producten</p>
                </div>
              </div>
            )}

            {canExportStatistics ? (
              <button
                onClick={() => setShowLocationLabelsModal(true)}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="font-medium text-gray-900">Exporteer Locatielabels</p>
                  <p className="text-sm text-gray-500">Download PDF met QR codes voor locaties</p>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <div className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 opacity-60 cursor-not-allowed">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="font-medium text-gray-900">Exporteer Locatielabels</p>
                  <p className="text-sm text-gray-500">Download PDF met QR codes voor locaties</p>
                </div>
              </div>
            )}

            {canViewProducts ? (
              <button
                onClick={handleProductStickersExport}
                disabled={exportingProductStickers}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="font-medium text-gray-900">{exportingProductStickers ? 'Bezig met exporteren...' : 'Exporteer Productstickers'}</p>
                  <p className="text-sm text-gray-500">Download PDF met QR codes voor producten</p>
                </div>
                {!exportingProductStickers && (
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
              </button>
            ) : (
              <div className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 opacity-60 cursor-not-allowed">
                <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="font-medium text-gray-900">Exporteer Productstickers</p>
                  <p className="text-sm text-gray-500">Download PDF met QR codes voor producten</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Location Labels Export Modal */}
      {showLocationLabelsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Locatielabels Exporteren</h2>
            <p className="text-gray-600 mb-4">
              Genereer een PDF met QR codes en codes voor alle locaties.
              De labels zijn ontworpen om geprint te worden en op schappen geplakt te worden.
            </p>

            <div className="mb-6">
              <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
                <input
                  type="radio"
                  name="locationLabelsType"
                  checked={locationLabelsLeafOnly}
                  onChange={() => setLocationLabelsLeafOnly(true)}
                  className="w-4 h-4 text-blue-600"
                />
                <div>
                  <p className="font-medium text-gray-900">Enkel laagste niveau</p>
                  <p className="text-sm text-gray-500">Alleen locaties zonder sublocaties (aanbevolen)</p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50 mt-2">
                <input
                  type="radio"
                  name="locationLabelsType"
                  checked={!locationLabelsLeafOnly}
                  onChange={() => setLocationLabelsLeafOnly(false)}
                  className="w-4 h-4 text-blue-600"
                />
                <div>
                  <p className="font-medium text-gray-900">Alle locaties</p>
                  <p className="text-sm text-gray-500">Inclusief hoofdlocaties en tussenliggende niveaus</p>
                </div>
              </label>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowLocationLabelsModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                disabled={exportingLocationLabels}
              >
                Annuleren
              </button>
              <button
                onClick={handleLocationLabelsExport}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={exportingLocationLabels}
              >
                {exportingLocationLabels ? 'Bezig met exporteren...' : 'Exporteren'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Options Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">PDF Export Opties</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Left column: Export options */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Opties</h3>
                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={exportOptions.showEstimatedStock}
                      onChange={(e) => setExportOptions({ ...exportOptions, showEstimatedStock: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Toon actuele geschatte voorraad</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={exportOptions.showConfirmedStock}
                      onChange={(e) => setExportOptions({ ...exportOptions, showConfirmedStock: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Toon voorraad van laatste telling</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={exportOptions.includeInactive}
                      onChange={(e) => setExportOptions({ ...exportOptions, includeInactive: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Toon inactieve producten</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={exportOptions.showManualColumn}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setExportOptions({
                          ...exportOptions,
                          showManualColumn: newValue,
                          // Disable product value if manual column is enabled
                          showProductValue: newValue ? false : exportOptions.showProductValue
                        });
                      }}
                      disabled={exportOptions.showProductValue}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <span className={`text-sm ${exportOptions.showProductValue ? 'text-gray-400' : 'text-gray-700'}`}>
                      Toon lege kolom (handig bij telling)
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={exportOptions.showProductValue}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setExportOptions({
                          ...exportOptions,
                          showProductValue: newValue,
                          // Disable manual column if product value is enabled
                          showManualColumn: newValue ? false : exportOptions.showManualColumn,
                          // Ensure at least one stock column is selected
                          showConfirmedStock: newValue ? (exportOptions.showConfirmedStock || exportOptions.showEstimatedStock ? exportOptions.showConfirmedStock : true) : exportOptions.showConfirmedStock,
                          showEstimatedStock: newValue ? (exportOptions.showConfirmedStock || exportOptions.showEstimatedStock ? exportOptions.showEstimatedStock : false) : exportOptions.showEstimatedStock
                        });
                      }}
                      disabled={exportOptions.showManualColumn}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <span className={`text-sm ${exportOptions.showManualColumn ? 'text-gray-400' : 'text-gray-700'}`}>
                      Toon productwaarde
                    </span>
                  </label>
                  {exportOptions.showProductValue && !exportOptions.showConfirmedStock && !exportOptions.showEstimatedStock && (
                    <div className="text-xs text-amber-600 mt-2 p-2 bg-amber-50 rounded">
                      Let op: Productwaarde vereist dat je ofwel "Bevestigde voorraad" of "Geschatte voorraad" aanvinkt.
                    </div>
                  )}
                </div>
              </div>

              {/* Right column: Brand selection */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Merken</h3>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedBrands.size === brands.length && brands.length > 0}
                      onChange={toggleAllBrands}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Alles</span>
                  </label>
                </div>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
                  {brands.map((brand) => (
                    <label key={brand.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={selectedBrands.has(brand.id)}
                        onChange={() => toggleBrand(brand.id)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{brand.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 border rounded hover:bg-gray-100"
                disabled={exportingPDF}
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={handleExportConfirm}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={selectedBrands.size === 0 || exportingPDF}
              >
                {exportingPDF ? 'Bezig met exporteren...' : 'Exporteren'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Total Stock Value Section - Only visible with statistics.manage_snapshots permission */}
      {canManageSnapshots && (
        <div className="mt-8">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow p-6 border border-blue-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Totale Voorraadwaarde
            </h2>

            {loadingStockValue ? (
              <p className="text-gray-600">Berekenen...</p>
            ) : stockValue ? (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Confirmed Value */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-600">Bevestigde Waarde</p>
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-4xl font-bold text-gray-900 mb-1">
                    €{formatNumber(stockValue.confirmed_total_value, 2)}
                  </p>
                  <p className="text-sm text-gray-500">
                    Gebaseerd op laatste voorraadtellingen
                  </p>
                </div>

                {/* Estimated Value */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-600">Geschatte Waarde</p>
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-4xl font-bold text-gray-900 mb-1">
                    €{formatNumber(stockValue.estimated_total_value, 2)}
                  </p>
                  <p className="text-sm text-gray-500">
                    Inclusief aankopen en verkopen sinds tellingen
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-gray-600">Kon voorraadwaarde niet laden</p>
            )}

            {stockValue && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  <p>
                    Berekend over {stockValue.product_count} actieve producten
                  </p>
                  {stockValue.snapshot_date && (
                    <p className="text-xs text-gray-500 mt-1">
                      Laatste update: {new Date(stockValue.snapshot_date).toLocaleString('nl-NL')}
                    </p>
                  )}
                </div>
                <button
                  onClick={refreshStockValue}
                  disabled={loadingStockValue}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {loadingStockValue ? 'Berekenen...' : 'Vernieuwen'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sales Statistics Section */}
      <div className="mt-8">
        {/* Top and Worst Sellers Lists */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Top Sellers */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                Top 10 Best Verkochte Producten
              </h2>
              <select
                value={topSellersPeriod}
                onChange={(e) => setTopSellersPeriod(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                {Object.entries(periodLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            {loadingTopSellers ? (
              <p className="text-gray-600">Laden...</p>
            ) : topSellers.length === 0 ? (
              <p className="text-gray-600">Geen verkopen in deze periode</p>
            ) : (
              <div className="space-y-2">
                {topSellers.map((product, index) => (
                  <Link
                    key={product.id}
                    to={`/products/${product.id}`}
                    className="flex items-center justify-between p-3 rounded hover:bg-gray-50 transition-colors border border-gray-100"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-lg font-bold text-gray-400 w-6 flex-shrink-0">
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{product.product_name}</p>
                        <p className="text-sm text-gray-500 truncate">{product.brand_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-lg font-bold text-green-600">
                        {formatNumber(product.total_quantity, 0)}
                      </span>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Worst Sellers */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                </svg>
                Top 10 Minst Verkochte Producten
              </h2>
              <select
                value={worstSellersPeriod}
                onChange={(e) => setWorstSellersPeriod(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                {Object.entries(periodLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            {loadingWorstSellers ? (
              <p className="text-gray-600">Laden...</p>
            ) : worstSellers.length === 0 ? (
              <p className="text-gray-600">Geen verkopen in deze periode</p>
            ) : (
              <div className="space-y-2">
                {worstSellers.map((product, index) => (
                  <Link
                    key={product.id}
                    to={`/products/${product.id}`}
                    className="flex items-center justify-between p-3 rounded hover:bg-gray-50 transition-colors border border-gray-100"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-lg font-bold text-gray-400 w-6 flex-shrink-0">
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{product.product_name}</p>
                        <p className="text-sm text-gray-500 truncate">{product.brand_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-lg font-bold text-orange-600">
                        {formatNumber(product.total_quantity, 0)}
                      </span>
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error Modal */}
      <ErrorModal
        show={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: "" })}
      />
    </div>
  );
}
